/** Small, accessible form primitives shared by the calculator and the registration wizard. */
import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { fieldDomId } from './registrationState';
import { sastParts } from '../../utils/sastDate';

export const inputClass = (hasError?: boolean) =>
  `w-full px-3.5 py-2.5 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
    hasError ? 'border-red-400' : 'border-gray-300'
  }`;

export function FieldError({ id, message }: { id?: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="mt-1.5 text-sm text-red-600 flex items-start gap-1.5">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </p>
  );
}

interface TextFieldProps {
  path: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error?: string;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel' | 'email';
  autoComplete?: string;
  placeholder?: string;
  hint?: ReactNode;
  multiline?: boolean;
  required?: boolean;
  readOnly?: boolean;
}

export function TextField({
  path, label, value, onChange, onBlur, error, type = 'text', inputMode, autoComplete, placeholder, hint, multiline, required = true, readOnly,
}: TextFieldProps) {
  const id = fieldDomId(path);
  const errId = `${id}-error`;
  const common = {
    id,
    name: path,
    value,
    placeholder,
    autoComplete,
    readOnly,
    required,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? errId : undefined,
    className: `${inputClass(!!error)} ${readOnly ? 'bg-gray-50 text-gray-600' : ''}`,
    onBlur,
  };
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </label>
      {multiline ? (
        <textarea {...common} rows={3} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input {...common} type={type} inputMode={inputMode} onChange={(e) => onChange(e.target.value)} />
      )}
      {hint && !error && <div className="mt-1.5 text-xs text-gray-500">{hint}</div>}
      <FieldError id={errId} message={error} />
    </div>
  );
}

interface NumberFieldProps {
  path: string;
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  error?: string;
  step?: string;
  min?: number;
  max?: number;
  suffix?: string;
}

export function NumberField({ path, label, value, onChange, error, step = '1', min, max, suffix }: NumberFieldProps) {
  const id = fieldDomId(path);
  const errId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          name={path}
          type="number"
          inputMode={step === '1' ? 'numeric' : 'decimal'}
          step={step}
          min={min}
          max={max}
          value={value === null || !isFinite(value) ? '' : value}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errId : undefined}
          className={`${inputClass(!!error)} ${suffix ? 'pr-9' : ''}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{suffix}</span>}
      </div>
      <FieldError id={errId} message={error} />
    </div>
  );
}

export function Checkbox({
  id, checked, onChange, children, error,
}: { id: string; checked: boolean; onChange: (v: boolean) => void; children: ReactNode; error?: string }) {
  return (
    <div>
      <label htmlFor={id} className="flex items-start gap-3 cursor-pointer select-none">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error ? true : undefined}
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary accent-[#3d7a24]"
        />
        <span className="text-sm text-gray-700 leading-relaxed">{children}</span>
      </label>
      <FieldError message={error} />
    </div>
  );
}

/** "Day / Month / Year" for today, read-only (POA and declaration). */
export function TodayDate({ label = 'Date' }: { label?: string }) {
  // The SA date — the signer's device may be in another zone.
  const d = sastParts(new Date());
  const parts: [string, string][] = [
    ['Day', String(d.day).padStart(2, '0')],
    ['Month', String(d.month).padStart(2, '0')],
    ['Year', String(d.year)],
  ];
  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1.5">{label}</span>
      <div className="flex gap-2">
        {parts.map(([k, v]) => (
          <div key={k} className="flex-1 max-w-[6rem] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-center">
            <div className="text-[11px] uppercase tracking-wide text-gray-400">{k}</div>
            <div className="font-semibold text-gray-800">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
